import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Send, MessageSquare, Plus, Loader2, Building2, Library, Sparkles, ExternalLink,
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { apiFetch } from '@/hooks/useFetch';
import { openInExternalBrowser } from '@/pages/SupportPage';

// `?fromApp=1` detection — sponsored-blog Stripe checkout must open
// in the device's external browser when inside the iOS/Android/Amazon
// app shell (same rule as the Support page).
function isInApp() {
  try {
    if (typeof window === 'undefined') return false;
    const url = new URL(window.location.href);
    if (url.searchParams.get('fromApp') === '1') {
      sessionStorage.setItem('cca_from_app', '1');
      return true;
    }
    return sessionStorage.getItem('cca_from_app') === '1';
  } catch { return false; }
}

const TABS = [
  { id: 'feedback',         icon: MessageSquare, en: 'Feedback & Issues',    es: 'Comentarios y problemas' },
  { id: 'phrase',           icon: Plus,          en: 'Request a Phrase',     es: 'Solicitar frase' },
  { id: 'organizations',    icon: Building2,     en: 'For Organizations',    es: 'Para organizaciones' },
  { id: 'submit-resource',  icon: Library,       en: 'Submit a Resource',    es: 'Enviar un recurso' },
];

const FeedbackPage = () => {
  const { t, isSpanish } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialTab = searchParams.get('tab') || 'feedback';
  const [activeTab, setActiveTab] = useState(
    TABS.find((tab) => tab.id === initialTab) ? initialTab : 'feedback'
  );

  // Keep URL ?tab=... synced for shareable deep-links
  useEffect(() => {
    const current = searchParams.get('tab');
    if (current !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  // Feedback form state
  const [feedbackForm, setFeedbackForm] = useState({ name: '', email: '', message: '' });

  // Phrase request form state
  const [phraseForm, setPhraseForm] = useState({
    name: '', email: '', phrase: '',
    literalMeaning: '', impliedMeaning: '', message: '',
  });

  // Organizations inquiry form state
  const [orgForm, setOrgForm] = useState({
    name: '', email: '', organization: '', role: '', message: '',
  });

  // Submit-a-resource form state
  const [resourceCategories, setResourceCategories] = useState([]);
  const [resourceForm, setResourceForm] = useState({
    submitter_name: '', submitter_email: '',
    name: '', url: '',
    selectedCategories: [],
    other_category: '',
    about: '',
    is_affiliated: false,
    wants_sponsored_blog: false,
  });

  // Load category list so submitters can pick which buckets the resource fits.
  useEffect(() => {
    apiFetch('/api/resources/categories').then(({ data }) => {
      if (data?.categories) setResourceCategories(data.categories);
    });
  }, []);

  // --- Submit handlers ---------------------------------------------

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { data, error } = await apiFetch('/api/feedback/submit', {
        method: 'POST',
        body: {
          name: feedbackForm.name,
          email: feedbackForm.email,
          feedback_type: 'feedback',
          message: feedbackForm.message,
        },
      });
      if (error) throw new Error('Failed to submit feedback');
      toast.success(isSpanish ? '¡Comentario enviado!' : 'Feedback submitted!', {
        description: data.message,
      });
      setFeedbackForm({ name: '', email: '', message: '' });
    } catch {
      toast.error(isSpanish ? 'Error al enviar' : 'Failed to submit', {
        description: isSpanish
          ? 'Intenta más tarde o envíanos un email.'
          : 'Please try again later or email us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhraseSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { data, error } = await apiFetch('/api/feedback/submit', {
        method: 'POST',
        body: {
          name: phraseForm.name,
          email: phraseForm.email,
          feedback_type: 'phrase_request',
          message: phraseForm.message,
          phrase: phraseForm.phrase,
          literal_meaning: phraseForm.literalMeaning,
          implied_meaning: phraseForm.impliedMeaning,
        },
      });
      if (error) throw new Error('Failed to submit phrase request');
      toast.success(
        isSpanish ? '¡Solicitud de frase enviada!' : 'Phrase request submitted!',
        { description: data.message },
      );
      setPhraseForm({
        name: '', email: '', phrase: '',
        literalMeaning: '', impliedMeaning: '', message: '',
      });
    } catch {
      toast.error(isSpanish ? 'Error al enviar' : 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOrgSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { data, error } = await apiFetch('/api/feedback/submit', {
        method: 'POST',
        body: {
          name: orgForm.name,
          email: orgForm.email,
          feedback_type: 'organization_inquiry',
          message:
            `Organization: ${orgForm.organization}\n` +
            `Role: ${orgForm.role}\n\n${orgForm.message}`,
        },
      });
      if (error) throw new Error('Failed to submit inquiry');
      toast.success(
        isSpanish ? '¡Consulta enviada!' : 'Inquiry submitted!',
        { description: data.message },
      );
      setOrgForm({ name: '', email: '', organization: '', role: '', message: '' });
    } catch {
      toast.error(isSpanish ? 'Error al enviar' : 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResourceSubmit = async (e) => {
    e.preventDefault();
    if (!resourceForm.name || !resourceForm.url) {
      toast.error(isSpanish ? 'Nombre y URL son obligatorios' : 'Name and URL are required');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        name: resourceForm.name,
        url: resourceForm.url,
        submitter_name: resourceForm.submitter_name,
        submitter_email: resourceForm.submitter_email,
        categories: resourceForm.selectedCategories,
        other_category: resourceForm.other_category || null,
        about: resourceForm.about,
        is_affiliated: resourceForm.is_affiliated,
        wants_sponsored_blog: resourceForm.is_affiliated && resourceForm.wants_sponsored_blog,
      };
      const { data, error } = await apiFetch('/api/resources/submit', {
        method: 'POST',
        body: payload,
      });
      if (error || !data?.ok) {
        throw new Error(error || 'Failed to submit resource');
      }
      toast.success(
        isSpanish
          ? '¡Recurso enviado! Lo revisaremos pronto.'
          : 'Resource submitted! We\'ll review it soon.',
      );
      // If they opted into the sponsored blog, route to checkout.
      // When inside the in-app webview shell, open the Stripe Payment
      // Link in the EXTERNAL browser to stay store-policy-compliant
      // (Apple/Google/Amazon all forbid in-webview payments for
      // non-IAP transactions). On the open web we redirect in-page.
      if (payload.wants_sponsored_blog && data.sponsored_checkout_url) {
        if (isInApp()) {
          openInExternalBrowser(data.sponsored_checkout_url);
        } else {
          window.location.href = data.sponsored_checkout_url;
        }
        return;
      }
      setResourceForm({
        submitter_name: '', submitter_email: '',
        name: '', url: '',
        selectedCategories: [], other_category: '',
        about: '', is_affiliated: false, wants_sponsored_blog: false,
      });
    } catch (err) {
      toast.error(isSpanish ? 'Error al enviar' : 'Failed to submit', {
        description: err?.message || '',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleResourceCategory = (cat) => {
    setResourceForm((prev) => {
      const exists = prev.selectedCategories.includes(cat);
      return {
        ...prev,
        selectedCategories: exists
          ? prev.selectedCategories.filter((c) => c !== cat)
          : [...prev.selectedCategories, cat],
      };
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <section className="py-10 sm:py-14 pb-24">
        <div className="container max-w-2xl">
          <div className="text-center mb-6">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">
              {isSpanish ? 'Contáctanos' : 'Contact'}
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base">
              {isSpanish
                ? 'Comentarios, frases nuevas, alianzas y recursos — todo en un solo lugar.'
                : 'Feedback, new phrases, partnerships and resources — all in one place.'}
            </p>
          </div>

          <Card>
            <CardHeader>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 gap-1 h-auto p-1">
                  {TABS.map(({ id, icon: Icon, en, es }) => (
                    <TabsTrigger
                      key={id}
                      value={id}
                      className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs leading-tight min-h-[3rem] sm:min-h-[2.5rem] px-1.5 py-1.5 whitespace-normal text-center"
                      data-testid={`feedback-tab-${id}`}
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="block">{isSpanish ? es : en}</span>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </CardHeader>

            <CardContent>
              {/* --- Feedback tab --- */}
              {activeTab === 'feedback' && (
                <form onSubmit={handleFeedbackSubmit} className="space-y-4" data-testid="feedback-form">
                  <div className="space-y-2">
                    <Label htmlFor="feedback-name">{t('yourName')}</Label>
                    <Input id="feedback-name" placeholder={t('enterName')}
                      value={feedbackForm.name}
                      onChange={(e) => setFeedbackForm((p) => ({ ...p, name: e.target.value }))}
                      required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="feedback-email">{t('emailAddress')}</Label>
                    <Input id="feedback-email" type="email" placeholder={t('emailPlaceholder')}
                      value={feedbackForm.email}
                      onChange={(e) => setFeedbackForm((p) => ({ ...p, email: e.target.value }))}
                      required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="feedback-message">{t('yourFeedback')}</Label>
                    <Textarea id="feedback-message" placeholder={t('feedbackPlaceholder')} rows={5}
                      value={feedbackForm.message}
                      onChange={(e) => setFeedbackForm((p) => ({ ...p, message: e.target.value }))}
                      required />
                  </div>
                  <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="feedback-submit-btn">
                    {isSubmitting
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('sending')}</>
                      : <><Send className="h-4 w-4 mr-2" /> {t('submitFeedback')}</>}
                  </Button>
                </form>
              )}

              {/* --- Phrase request tab --- */}
              {activeTab === 'phrase' && (
                <form onSubmit={handlePhraseSubmit} className="space-y-4" data-testid="phrase-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phrase-name">{t('yourName')}</Label>
                      <Input id="phrase-name" placeholder={t('enterName')}
                        value={phraseForm.name}
                        onChange={(e) => setPhraseForm((p) => ({ ...p, name: e.target.value }))}
                        required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phrase-email">{t('emailAddress')}</Label>
                      <Input id="phrase-email" type="email" placeholder={t('emailPlaceholder')}
                        value={phraseForm.email}
                        onChange={(e) => setPhraseForm((p) => ({ ...p, email: e.target.value }))}
                        required />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phrase-text">{t('phrase')}</Label>
                    <Input id="phrase-text" placeholder={t('phrasePlaceholder')}
                      value={phraseForm.phrase}
                      onChange={(e) => setPhraseForm((p) => ({ ...p, phrase: e.target.value }))}
                      required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="literal-meaning">{t('literalMeaning')} {t('optional')}</Label>
                    <Input id="literal-meaning" placeholder={t('literalMeaningPlaceholder')}
                      value={phraseForm.literalMeaning}
                      onChange={(e) => setPhraseForm((p) => ({ ...p, literalMeaning: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="implied-meaning">{t('impliedMeaning')} {t('optional')}</Label>
                    <Input id="implied-meaning" placeholder={t('impliedMeaningPlaceholder')}
                      value={phraseForm.impliedMeaning}
                      onChange={(e) => setPhraseForm((p) => ({ ...p, impliedMeaning: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phrase-notes">{t('additionalNotes')} {t('optional')}</Label>
                    <Textarea id="phrase-notes" placeholder={t('notesPlaceholder')} rows={3}
                      value={phraseForm.message}
                      onChange={(e) => setPhraseForm((p) => ({ ...p, message: e.target.value }))} />
                  </div>
                  <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="phrase-submit-btn">
                    {isSubmitting
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('sending')}</>
                      : <><Send className="h-4 w-4 mr-2" /> {t('submitPhraseRequest')}</>}
                  </Button>
                </form>
              )}

              {/* --- Organizations tab --- */}
              {activeTab === 'organizations' && (
                <form onSubmit={handleOrgSubmit} className="space-y-4" data-testid="org-form">
                  <p className="text-sm text-muted-foreground">
                    {isSpanish
                      ? 'Trabajamos con escuelas, terapeutas, lugares de trabajo y organizaciones sin fines de lucro.'
                      : 'We work with schools, therapists, workplaces and nonprofits.'}{' '}
                    <button type="button" onClick={() => navigate('/for-organizations')}
                      className="underline text-[#00A896] hover:text-[#008B7C] inline-flex items-center gap-1"
                      data-testid="org-page-link">
                      {isSpanish ? 'Más detalles' : 'See full details'}
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="org-name">{t('yourName')}</Label>
                      <Input id="org-name" value={orgForm.name}
                        onChange={(e) => setOrgForm((p) => ({ ...p, name: e.target.value }))} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="org-email">{t('emailAddress')}</Label>
                      <Input id="org-email" type="email" value={orgForm.email}
                        onChange={(e) => setOrgForm((p) => ({ ...p, email: e.target.value }))} required />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="org-organization">
                        {isSpanish ? 'Organización' : 'Organization'}
                      </Label>
                      <Input id="org-organization" value={orgForm.organization}
                        onChange={(e) => setOrgForm((p) => ({ ...p, organization: e.target.value }))} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="org-role">
                        {isSpanish ? 'Tu rol' : 'Your role'}
                      </Label>
                      <Input id="org-role" value={orgForm.role}
                        onChange={(e) => setOrgForm((p) => ({ ...p, role: e.target.value }))} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="org-message">
                      {isSpanish ? '¿Cómo podemos ayudar?' : 'How can we help?'}
                    </Label>
                    <Textarea id="org-message" rows={5} value={orgForm.message}
                      onChange={(e) => setOrgForm((p) => ({ ...p, message: e.target.value }))} required />
                  </div>
                  <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="org-submit-btn">
                    {isSubmitting
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('sending')}</>
                      : <><Send className="h-4 w-4 mr-2" /> {isSpanish ? 'Enviar consulta' : 'Submit inquiry'}</>}
                  </Button>
                </form>
              )}

              {/* --- Submit-a-resource tab --- */}
              {activeTab === 'submit-resource' && (
                <form onSubmit={handleResourceSubmit} className="space-y-4" data-testid="resource-form">
                  <p className="text-sm text-muted-foreground">
                    {isSpanish
                      ? 'Comparte un recurso útil para la comunidad neurodivergente. Revisamos cada envío antes de publicarlo.'
                      : 'Share a resource useful to the neurodivergent community. We review every submission before listing.'}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="res-resource-name">
                        {isSpanish ? 'Nombre del recurso' : 'Resource name'}
                      </Label>
                      <Input id="res-resource-name" placeholder="e.g. ClauseMap"
                        value={resourceForm.name}
                        onChange={(e) => setResourceForm((p) => ({ ...p, name: e.target.value }))}
                        required data-testid="res-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="res-url">URL</Label>
                      <Input id="res-url" type="url" placeholder="https://…"
                        value={resourceForm.url}
                        onChange={(e) => setResourceForm((p) => ({ ...p, url: e.target.value }))}
                        required data-testid="res-url-input" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="res-submitter-name">{t('yourName')}</Label>
                      <Input id="res-submitter-name"
                        value={resourceForm.submitter_name}
                        onChange={(e) => setResourceForm((p) => ({ ...p, submitter_name: e.target.value }))}
                        required data-testid="res-submitter-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="res-submitter-email">{t('emailAddress')}</Label>
                      <Input id="res-submitter-email" type="email"
                        value={resourceForm.submitter_email}
                        onChange={(e) => setResourceForm((p) => ({ ...p, submitter_email: e.target.value }))}
                        required data-testid="res-submitter-email-input" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{isSpanish ? 'Categorías (selecciona varias)' : 'Categories (pick any that fit)'}</Label>
                    <div className="flex flex-wrap gap-2" data-testid="res-categories-list">
                      {resourceCategories.map((cat) => {
                        const checked = resourceForm.selectedCategories.includes(cat);
                        return (
                          <label key={cat}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs border cursor-pointer transition-colors ${
                              checked
                                ? 'bg-[#00C9B3] text-[#0d1f2d] border-[#00C9B3]'
                                : 'bg-muted/40 border-border hover:bg-muted'
                            }`}>
                            <Checkbox checked={checked}
                              onCheckedChange={() => toggleResourceCategory(cat)}
                              data-testid={`res-cat-${cat}`}
                              className="h-3.5 w-3.5" />
                            {cat}
                          </label>
                        );
                      })}
                    </div>
                    <div className="pt-1">
                      <Label htmlFor="res-other-cat" className="text-xs text-muted-foreground">
                        {isSpanish ? 'Otra (escribe tu propia categoría)' : 'Other (type your own)'}
                      </Label>
                      <Input id="res-other-cat" placeholder={isSpanish ? 'Otra categoría…' : 'Other category…'}
                        value={resourceForm.other_category}
                        onChange={(e) => setResourceForm((p) => ({ ...p, other_category: e.target.value }))}
                        data-testid="res-other-cat-input" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="res-about">
                      {isSpanish
                        ? 'Cuéntanos sobre el recurso y qué te resulta útil'
                        : 'Tell us about the resource and what you find useful'}
                    </Label>
                    <Textarea id="res-about" rows={4}
                      placeholder={isSpanish
                        ? '¿Qué problema resuelve? ¿Para quién es útil? ¿Por qué lo recomendarías?'
                        : 'What problem does it solve? Who is it useful for? Why would you recommend it?'}
                      value={resourceForm.about}
                      onChange={(e) => setResourceForm((p) => ({ ...p, about: e.target.value.slice(0, 2000) }))}
                      data-testid="res-about-input" />
                    <p className="text-[10px] text-muted-foreground">{resourceForm.about.length} / 2000</p>
                  </div>

                  <div className="rounded-md border bg-muted/20 p-3 space-y-3">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Checkbox
                        checked={resourceForm.is_affiliated}
                        onCheckedChange={(v) => setResourceForm((p) => ({
                          ...p,
                          is_affiliated: !!v,
                          // Reset the sponsored-blog choice if affiliation is unchecked.
                          wants_sponsored_blog: v ? p.wants_sponsored_blog : false,
                        }))}
                        data-testid="res-affiliation-checkbox"
                      />
                      <div className="text-sm">
                        <div className="font-medium">
                          {isSpanish
                            ? '¿Estás afiliado/a con este recurso?'
                            : 'Are you affiliated with this resource?'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {isSpanish
                            ? '¡Totalmente bienvenido — solo es para transparencia!'
                            : 'Totally welcome — this is just for transparency!'}
                        </div>
                      </div>
                    </label>

                    {resourceForm.is_affiliated && (
                      <label
                        className="flex items-start gap-2 cursor-pointer rounded-md bg-gradient-to-br from-[#00E5CC]/10 to-purple-500/10 border border-[#00E5CC]/30 p-2.5"
                        data-testid="res-sponsored-blog-wrap"
                      >
                        <Checkbox
                          checked={resourceForm.wants_sponsored_blog}
                          onCheckedChange={(v) => setResourceForm((p) => ({ ...p, wants_sponsored_blog: !!v }))}
                          data-testid="res-sponsored-blog-checkbox"
                        />
                        <div className="text-sm">
                          <div className="font-semibold flex items-center gap-1.5">
                            <Sparkles className="h-3.5 w-3.5 text-[#7C3AED]" />
                            {isSpanish
                              ? '¿Te gustaría un artículo dedicado en nuestro blog? ($29)'
                              : 'Would you like a dedicated blog article about your resource? ($29)'}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {isSpanish
                              ? 'Mejora tu SEO y ayuda a más personas neurodivergentes a descubrirte. Te llevaremos al pago después de enviar.'
                              : 'Boost your SEO and help more neurodivergent users find you. You\'ll be sent to checkout after submitting.'}
                          </div>
                        </div>
                      </label>
                    )}
                  </div>

                  <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="res-submit-btn">
                    {isSubmitting
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('sending')}</>
                      : <><Send className="h-4 w-4 mr-2" />
                          {resourceForm.is_affiliated && resourceForm.wants_sponsored_blog
                            ? (isSpanish ? 'Enviar y continuar al pago' : 'Submit & continue to checkout')
                            : (isSpanish ? 'Enviar recurso' : 'Submit resource')}
                        </>}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {t('emailDirectly')}{' '}
            <a href="mailto:Support@ClearCommunicationApp.com" className="text-primary hover:underline">
              Support@ClearCommunicationApp.com
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default FeedbackPage;
